/**
 * @copyright (C) 2023, Rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of Rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by Rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 **/

import React from "react";

interface ErrorBoundaryProps {
	onError?: (error: Error, info: React.ErrorInfo) => void;
	resetKeys?: Array<unknown>;
	fallback?: React.ReactElement<unknown, string | React.FunctionComponent | typeof React.Component> | null;
}
type ErrorBoundaryState = { error: Error | null };

const initialState: ErrorBoundaryState = { error: null };
const changedArray = (prevArray: Array<unknown> = [], nextArray: Array<unknown> = []) =>
	prevArray.length !== nextArray.length || prevArray.some((item, index) => !Object.is(item, nextArray[index]));

export default class ErrorBoundary extends React.Component<
	React.PropsWithRef<React.PropsWithChildren<ErrorBoundaryProps>>,
	ErrorBoundaryState
> {
	static getDerivedStateFromError(error: Error) {
		return { error };
	}

	state = initialState;

	resetErrorBoundary = () => {
		this.reset();
	};

	reset() {
		this.setState(initialState);
	}

	componentDidCatch(error: Error, info: React.ErrorInfo) {
		this.props.onError?.(error, info);
	}

	componentDidUpdate(prevProps: ErrorBoundaryProps, prevState: ErrorBoundaryState) {
		const { error } = this.state;
		const { resetKeys } = this.props;

		if (error !== null && prevState.error !== null && changedArray(prevProps.resetKeys, resetKeys)) {
			this.reset();
		}
	}

	render() {
		const { error } = this.state;
		const { fallback, children } = this.props;
		if (error !== null) {
			const props = {
				error,
				resetErrorBoundary: this.resetErrorBoundary
			};
			if (React.isValidElement(fallback)) {
				return fallback;
			}
			props.resetErrorBoundary();
		}
		return children;
	}
}
